/**
 * files/files.js
 * File upload handling and attachment management
 * Lazy-loaded module for file operations
 */

import { state } from '../state.js?v=302';
import { elements } from '../dom.js?v=302';
import { icons } from '../icons.js?v=302';
import { escapeHtml, formatFileSize } from '../utils.js?v=302';
import { showToast, announceToScreenReader } from '../ui.js?v=302';
import { processFile } from './processors.js?v=302';

/**
 * Handle file upload from input element
 * Processes multiple files and updates attachment list
 * @param {Event} e - File input change event
 */
export async function handleFileUpload(e) {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;

  for (const file of files) {
    try {
      const fileData = await processFile(file);
      state.attachedFiles.push(fileData);
    } catch (error) {
      console.error("Error processing file:", error);
      showToast(`Error processing ${file.name}`, "error");
    }
  }

  updateAttachments(state.attachedFiles);
  elements.fileInput.value = "";
}

/**
 * Update attachments display in UI
 * Shows attachment pills with icons, names, sizes, and remove buttons
 * @param {Array} files - Array of file data objects
 */
export function updateAttachments(files) {
  state.attachedFiles = files;

  if (files.length === 0) {
    elements.attachmentsList.classList.remove("has-attachments");
    elements.attachmentsList.innerHTML = "";
    return;
  }

  elements.attachmentsList.innerHTML = files
    .map((file, index) => {
      let icon = icons.file;
      if (file.type === "pdf") icon = icons.pdf;
      if (file.type === "docx") icon = icons.docx;
      if (file.type === "xlsx" || file.type === "xls") icon = icons.xlsx;

      // Code/Text file types
      if (["txt", "md", "csv", "json", "xml", "html", "css", "js", "py", "java", "c", "cpp", "ts", "go", "rs", "rb", "php", "swift", "kt", "yaml", "yml", "toml", "ini", "env"].includes(file.type)) {
        icon = icons.code;
      }

      // Image file types
      if (["jpg", "jpeg", "png", "gif", "webp", "svg", "ico", "bmp"].includes(file.type)) {
        icon = icons.image;
      }

      // Archive file types
      if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz"].includes(file.type)) {
        icon = icons.archive;
      }

      // PowerPoint
      if (file.type === "pptx") {
        icon = icons.pptx;
      }

      return `
            <div class="attachment-pill">
                <span class="attachment-icon">${icon}</span>
                <div style="display: flex; flex-direction: column; gap: 0;">
                  <span class="attachment-name">${escapeHtml(file.name)}</span>
                  <span style="font-size: 10px; color: var(--text-tertiary);">${formatFileSize(file.size)}</span>
                </div>
                <button class="attachment-remove" data-index="${index}" aria-label="Remove attachment ${escapeHtml(file.name)}">${icons.x}</button>
            </div>
        `;
    })
    .join("");

  elements.attachmentsList.classList.add("has-attachments");

  // Remove attachment handlers
  elements.attachmentsList
    .querySelectorAll(".attachment-remove")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const index = parseInt(btn.dataset.index);
        const fileName = state.attachedFiles[index]?.name || 'file';
        state.attachedFiles.splice(index, 1);
        updateAttachments(state.attachedFiles);
        announceToScreenReader(`Removed attachment ${fileName}`);
      });
    });

  // Announce attachment to screen readers
  if (files.length > 0) {
    const lastFile = files[files.length - 1];
    announceToScreenReader(`Attached ${lastFile.name}`);
  }
}
